import { Alert } from 'antd'

import MapFieldsTable from './MapFieldsTable'
import type {
  MatchFieldsActions,
  MatchFieldsState,
} from '../../hooks/useMatchFieldsReducer'

function MapFields({
  state,
  actions,
  hideRequiredWarning = false,
}: {
  state: Partial<MatchFieldsState>
  actions: Partial<MatchFieldsActions>
  /**
   * Suppresses the inline "these properties must be mapped" notice.
   *
   * The feed dialog shows it in its footer instead: inline, it appears and
   * disappears as the user maps, and the table jumps under the cursor each
   * time. Hosts without a footer — the file-import flows — keep it here.
   */
  hideRequiredWarning?: boolean
}): JSX.Element {
  const {
    requiredNotSelected = [],
    options = [],
    allSelected = [],
    matchedProperties = [],
    matchedSelected = [],
    unmatchedProperties = [],
    unmatchedSelected = [],
  } = state

  const {
    setMatchedSelected,
    removeMatchedSelected,
    setUnmatchedSelected,
    removeUnmatchedSelected,
  } = actions

  return (
    <>
      <div className="w-full">
        {!matchedProperties || !matchedProperties.length ? (
          <></>
        ) : (
          <div className="mt-8 flex flex-col">
            <h3 className="font-medium text-lg text-gray-900 mb-1">
              Columns with matching properties
            </h3>
            <p className="font-normal text-sm text-gray-500">
              These columns match some standard Prisync properties. If one of
              these properties was incorrectly mapped, please map it to the
              correct Prisync property.
            </p>
            {!hideRequiredWarning && requiredNotSelected?.length > 0 && (
              <div className="mt-2 w-full">
                <span className="text-red-500 text-sm">
                  These properties (
                  {requiredNotSelected.map((i) => i.text).join(', ')}) must be
                  mapped to properly import your data.
                </span>
              </div>
            )}
            <MapFieldsTable
              properties={matchedProperties}
              selected={matchedSelected}
              allSelected={allSelected}
              removeItem={removeMatchedSelected!}
              options={options}
              setSelected={setMatchedSelected!}
            />
          </div>
        )}
      </div>
      <div className="w-full">
        {!unmatchedProperties || !unmatchedProperties.length ? (
          <></>
        ) : (
          <div className="mt-8 flex flex-col">
            <h3 className="font-medium text-lg text-gray-900 mb-1">
              Columns with unmatched fields
            </h3>
            <p className="font-normal text-sm text-gray-500">
              No matches were found for the columns below. You can map each one
              to existing Prisync properties or skip them.
            </p>
            <MapFieldsTable
              properties={unmatchedProperties}
              selected={unmatchedSelected}
              allSelected={allSelected}
              removeItem={removeUnmatchedSelected!}
              options={options}
              setSelected={setUnmatchedSelected!}
            />
          </div>
        )}
      </div>

      <Alert
        type="warning"
        showIcon
        title="Note"
        description="Unmatched and unchecked columns will be ignored during the process."
        classNames={{
          root: 'mt-4 w-full border border-solid border-warning-300 rounded-lg bg-warning-25',
          title: 'font-medium text-sm !text-warning-700',
          description: 'font-normal text-sm !text-warning-600',
        }}
      />

      {!hideRequiredWarning && requiredNotSelected?.length > 0 && (
        <div className="mt-2 w-full">
          <span className="text-red-500 text-sm">
            These properties (
            {requiredNotSelected.map((i) => i.text).join(', ')}) must be mapped
            to properly import your data.
          </span>
        </div>
      )}
    </>
  )
}

export default MapFields
